<template>
  <div class="bid-kanban">

    <!-- Header -->
    <div class="kanban-header">
      <span class="kanban-title">Bid Checklist</span>
      <div class="kanban-meta">
        <span v-if="items.length" class="kanban-progress">
          {{ doneItems.length }}/{{ items.length }} done
        </span>
        <div class="kanban-progress-bar" v-if="items.length">
          <div
            class="kanban-progress-fill"
            :style="{ width: (doneItems.length / items.length * 100) + '%' }"
          ></div>
        </div>
      </div>
    </div>

    <!-- Add item row -->
    <div class="kanban-add-row">
      <input
        v-model="newItem"
        @keyup.enter="addItem"
        class="kanban-input"
        placeholder="Add a checklist item and press Enter…"
      />
      <button class="kanban-add-btn" @click="addItem" :disabled="adding">
        <span v-if="adding" class="spinner-border spinner-border-sm"></span>
        <span v-else><i class="fas fa-plus"></i> Add</span>
      </button>
    </div>

    <!-- Board columns -->
    <div class="kanban-board">

      <!-- To Do column -->
      <div
        class="kanban-col"
        :class="{ 'kanban-col--drag-over': dragOverCol === 'todo' }"
        @dragover.prevent="dragOverCol = 'todo'"
        @dragleave="onDragLeave('todo')"
        @drop.prevent="onDrop('todo')"
      >
        <div class="kanban-col-header kanban-col-header--todo">
          <span class="kanban-col-dot kanban-col-dot--todo"></span>
          <span class="kanban-col-label">To Do</span>
          <span class="kanban-col-count">{{ todoItems.length }}</span>
        </div>

        <div class="kanban-cards">
          <div
            v-for="item in todoItems"
            :key="item.id"
            class="kanban-card kanban-card--todo"
            draggable="true"
            @dragstart="onDragStart(item)"
            @dragend="dragItem = null"
          >
            <div class="kanban-card-body">
              <i class="fas fa-grip-vertical kanban-drag-handle"></i>
              <span class="kanban-card-text">{{ item.itemName }}</span>
            </div>
            <div class="kanban-card-actions">
              <button
                class="kanban-move-btn kanban-move-btn--forward"
                @click="toggle(item)"
                title="Mark as Done"
              >
                Done <i class="fas fa-check"></i>
              </button>
              <button
                class="kanban-del-btn"
                @click="remove(item)"
                :disabled="deleting[item.id]"
                title="Delete"
              >
                <span v-if="deleting[item.id]" class="spinner-border spinner-border-sm"></span>
                <i v-else class="fas fa-trash-alt"></i>
              </button>
            </div>
          </div>

          <div v-if="todoItems.length === 0" class="kanban-empty">
            <i class="fas fa-inbox kanban-empty-icon"></i>
            <span>No items to do</span>
          </div>
        </div>
      </div>

      <!-- Done column -->
      <div
        class="kanban-col"
        :class="{ 'kanban-col--drag-over': dragOverCol === 'done' }"
        @dragover.prevent="dragOverCol = 'done'"
        @dragleave="onDragLeave('done')"
        @drop.prevent="onDrop('done')"
      >
        <div class="kanban-col-header kanban-col-header--done">
          <span class="kanban-col-dot kanban-col-dot--done"></span>
          <span class="kanban-col-label">Done</span>
          <span class="kanban-col-count">{{ doneItems.length }}</span>
        </div>

        <div class="kanban-cards">
          <div
            v-for="item in doneItems"
            :key="item.id"
            class="kanban-card kanban-card--done"
            draggable="true"
            @dragstart="onDragStart(item)"
            @dragend="dragItem = null"
          >
            <div class="kanban-card-body">
              <i class="fas fa-grip-vertical kanban-drag-handle"></i>
              <span class="kanban-card-text kanban-card-text--done">{{ item.itemName }}</span>
            </div>
            <div class="kanban-card-actions">
              <button
                class="kanban-move-btn kanban-move-btn--back"
                @click="toggle(item)"
                title="Move back to To Do"
              >
                <i class="fas fa-undo"></i> Undo
              </button>
              <button
                class="kanban-del-btn"
                @click="remove(item)"
                :disabled="deleting[item.id]"
                title="Delete"
              >
                <span v-if="deleting[item.id]" class="spinner-border spinner-border-sm"></span>
                <i v-else class="fas fa-trash-alt"></i>
              </button>
            </div>
          </div>

          <div v-if="doneItems.length === 0" class="kanban-empty">
            <i class="fas fa-check-circle kanban-empty-icon"></i>
            <span>Nothing completed yet</span>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import bidApi from '@/api/bid'

export default {
  name: 'BidChecklist',
  props: {
    preparationId: { type: [Number, String], default: null },
    opportunityId: { type: [Number, String], default: null },
  },
  setup(props) {
    const items = ref([])
    const newItem = ref('')
    const adding = ref(false)
    const deleting = reactive({})
    const loading = ref(false)
    const dragItem = ref(null)
    const dragOverCol = ref(null)

    const todoItems = computed(() => items.value.filter(i => !i.isCompleted))
    const doneItems = computed(() => items.value.filter(i => i.isCompleted))

    const load = async () => {
      loading.value = true
      try {
        let prepId = props.preparationId
        if (!prepId && props.opportunityId) {
          try {
            const res = await bidApi.updatePreparation(props.opportunityId, {})
            prepId = res?.preparation?.id ?? null
          } catch (e) {
            prepId = null
          }
        }
        if (!prepId) { items.value = []; return }
        const list = await bidApi.listChecklists(prepId)
        items.value = Array.isArray(list) ? list : []
      } catch (err) {
        console.error('Failed to load checklist', err)
      } finally {
        loading.value = false
      }
    }

    const addItem = async () => {
      if (!newItem.value.trim() || adding.value) return
      adding.value = true
      try {
        let prepId = props.preparationId
        if (!prepId && props.opportunityId) {
          const res = await bidApi.updatePreparation(props.opportunityId, {})
          prepId = res?.preparation?.id ?? null
        }
        if (!prepId) throw new Error('Preparation ID not available')
        const created = await bidApi.createChecklist(prepId, newItem.value.trim())
        const item = created?.data || created?.item || created?.checklist || created
        if (item) items.value.push(item)
        newItem.value = ''
      } catch (err) {
        console.error('Failed to add checklist item', err)
      } finally {
        adding.value = false
      }
    }

    const toggle = async (item) => {
      const next = !item.isCompleted
      item.isCompleted = next
      try {
        await bidApi.updateChecklist(item.id, { isCompleted: next })
      } catch (err) {
        console.error('Failed to toggle checklist', err)
        item.isCompleted = !next
      }
    }

    const remove = async (item) => {
      deleting[item.id] = true
      try {
        await bidApi.deleteChecklist(item.id)
        items.value = items.value.filter(i => i.id !== item.id)
      } catch (err) {
        console.error('Failed to delete checklist', err)
      } finally {
        deleting[item.id] = false
      }
    }

    const onDragStart = (item) => {
      dragItem.value = item
    }

    const onDragLeave = (col) => {
      if (dragOverCol.value === col) dragOverCol.value = null
    }

    const onDrop = async (targetCol) => {
      dragOverCol.value = null
      const item = dragItem.value
      if (!item) return
      const shouldBeDone = targetCol === 'done'
      if (item.isCompleted === shouldBeDone) return
      await toggle(item)
    }

    onMounted(load)

    return {
      items, todoItems, doneItems,
      newItem, addItem, toggle, remove,
      adding, deleting, loading,
      dragItem, dragOverCol,
      onDragStart, onDragLeave, onDrop,
    }
  },
}
</script>

<style scoped>
/* ── Wrapper ───────────────────────────────────────── */
.bid-kanban {
  background: #f4f5f7;
  border-radius: 10px;
  padding: 16px 18px;
}

/* ── Header ────────────────────────────────────────── */
.kanban-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 11px;
  gap: 16px;
}
.kanban-title {
  font-weight: 700;
  font-size: 13px;
  color: #172b4d;
  white-space: nowrap;
}
.kanban-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  justify-content: flex-end;
}
.kanban-progress {
  font-size: 11px;
  font-weight: 600;
  color: #5e6c84;
  white-space: nowrap;
}
.kanban-progress-bar {
  flex: 1;
  max-width: 180px;
  height: 6px;
  background: #dfe1e6;
  border-radius: 99px;
  overflow: hidden;
}
.kanban-progress-fill {
  height: 100%;
  background: #36b37e;
  border-radius: 99px;
  transition: width 0.3s ease;
}

/* ── Add row ───────────────────────────────────────── */
.kanban-add-row {
  display: flex;
  gap: 8px;
  margin-bottom: 13px;
}
.kanban-input {
  flex: 1;
  background: #fff;
  border: 2px solid #dfe1e6;
  border-radius: 6px;
  padding: 7px 11px;
  font-size: 11px;
  color: #172b4d;
  transition: border-color 0.15s;
}
.kanban-input:focus {
  outline: none;
  border-color: #4c9aff;
  box-shadow: 0 0 0 2px rgba(76, 154, 255, 0.2);
}
.kanban-add-btn {
  background: #0052cc;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 7px 16px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s;
}
.kanban-add-btn:hover:not(:disabled) { background: #0747a6; }
.kanban-add-btn:disabled { opacity: 0.6; cursor: default; }

/* ── Board layout ──────────────────────────────────── */
.kanban-board {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 13px;
}

/* ── Column ────────────────────────────────────────── */
.kanban-col {
  background: #ebecf0;
  border-radius: 8px;
  padding: 10px;
  min-height: 120px;
  border: 2px solid transparent;
  transition: border-color 0.15s, background 0.15s;
}
.kanban-col--drag-over {
  border-color: #4c9aff;
  background: #e8f0fe;
}

.kanban-col-header {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 4px 4px 10px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}
.kanban-col-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}
.kanban-col-dot--todo { background: #ff991f; }
.kanban-col-dot--done { background: #36b37e; }
.kanban-col-header--todo { color: #974f0c; }
.kanban-col-header--done { color: #006644; }
.kanban-col-count {
  margin-left: auto;
  background: rgba(0,0,0,0.12);
  border-radius: 99px;
  padding: 1px 8px;
  font-size: 11px;
  font-weight: 700;
  color: inherit;
}

/* ── Cards ─────────────────────────────────────────── */
.kanban-cards {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.kanban-card {
  background: #fff;
  border-radius: 6px;
  padding: 10px 11px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.12);
  cursor: grab;
  transition: box-shadow 0.15s, transform 0.1s;
  border-left: 3px solid transparent;
}
.kanban-card:active { cursor: grabbing; }
.kanban-card:hover { box-shadow: 0 3px 8px rgba(0,0,0,0.16); transform: translateY(-1px); }
.kanban-card--todo { border-left-color: #ff991f; }
.kanban-card--done { border-left-color: #36b37e; opacity: 0.85; }

.kanban-card-body {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
}
.kanban-drag-handle {
  color: #c1c7d0;
  font-size: 11px;
  margin-top: 2px;
  flex-shrink: 0;
}
.kanban-card-text {
  font-size: 11px;
  color: #172b4d;
  line-height: 1.4;
  word-break: break-word;
}
.kanban-card-text--done {
  text-decoration: line-through;
  color: #7a869a;
}

.kanban-card-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  justify-content: flex-end;
}

.kanban-move-btn {
  border: none;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 10px;
  cursor: pointer;
  transition: background 0.15s;
}
.kanban-move-btn--forward {
  background: #e3fcef;
  color: #006644;
}
.kanban-move-btn--forward:hover { background: #abf5d1; }
.kanban-move-btn--back {
  background: #fff4e5;
  color: #974f0c;
}
.kanban-move-btn--back:hover { background: #ffe2bd; }

.kanban-del-btn {
  background: transparent;
  border: none;
  color: #c1c7d0;
  cursor: pointer;
  padding: 3px 6px;
  border-radius: 4px;
  font-size: 11px;
  transition: color 0.15s, background 0.15s;
}
.kanban-del-btn:hover:not(:disabled) { color: #de350b; background: #ffebe6; }
.kanban-del-btn:disabled { opacity: 0.4; cursor: default; }

/* ── Empty state ───────────────────────────────────── */
.kanban-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 20px 0;
  color: #97a0af;
  font-size: 11px;
}
.kanban-empty-icon {
  font-size: 20px;
  opacity: 0.5;
}
</style>
